import type {
  StatsAggDefUpdateDto,
  StatsAggRunRequestDto,
  StatsAggRunResponseDto,
} from '../../types/api/stats';
import { BaseServiceClient } from '../BaseServiceClient';

interface StatsAggReportServiceOptions {
  userStore?: any;
  storage?: any;
}

const EMPTY_RUN_RESPONSE: StatsAggRunResponseDto = { AccountId: '', ReportId: '', Items: [] };

/**
 * Stats > Aggregate report definitions (create/update/delete via stats_agg_def_put, keyed by
 * StatsAggReportDefDto.Status) and running a saved definition against live data
 * (stats_agg_get). Both requests ride the 'data' service channel, matching StatsUsageService's
 * stats_usage_get convention.
 */
export class StatsAggReportService extends BaseServiceClient {
  constructor(apiService: any) {
    super(apiService);
  }

  /**
   * Create ('2'), update ('1'), or delete ('-1') one report definition — see
   * StatsAggReportDefDto.Status. Returns the persisted definition (a newly-created def comes
   * back with its assigned ReportId, same as PracSiteUpdateDto/PracSraUpdateDto).
   */
  async putDef(
    request: StatsAggDefUpdateDto,
    options?: StatsAggReportServiceOptions
  ): Promise<StatsAggDefUpdateDto> {
    const response = await this.callService('stats_agg_def_put', request, 'data', {
      userStore: options?.userStore,
      storage: options?.storage,
    });

    const success = Boolean((response as any)?.success ?? (response as any)?.Success);
    if (!success) {
      throw new Error((response as any)?.message || (response as any)?.errorDescription || 'stats_agg_def_put failed');
    }

    const payload = this.unwrapPayload(response);
    return this.parseJsonPayload<StatsAggDefUpdateDto>(payload, request);
  }

  /** Runs one saved report definition against the given runtime filter values. */
  async runReport(
    request: StatsAggRunRequestDto,
    options?: StatsAggReportServiceOptions
  ): Promise<StatsAggRunResponseDto> {
    const response = await this.callService('stats_agg_get', request, 'data', {
      userStore: options?.userStore,
      storage: options?.storage,
    });

    const success = Boolean((response as any)?.success ?? (response as any)?.Success);
    if (!success) {
      throw new Error((response as any)?.message || (response as any)?.errorDescription || 'stats_agg_get failed');
    }

    const payload = this.unwrapPayload(response);
    return { ...EMPTY_RUN_RESPONSE, ...this.parseJsonPayload<StatsAggRunResponseDto>(payload, EMPTY_RUN_RESPONSE) };
  }

  private unwrapPayload(response: unknown): unknown {
    return (response as any)?.payload
      ?? (response as any)?.Payload
      ?? (response as any)?.result
      ?? (response as any)?.Result
      ?? (response as any)?.data
      ?? (response as any)?.Data
      ?? response;
  }

  private parseJsonPayload<T>(payload: unknown, fallback: T): T {
    if (typeof payload === 'string') {
      try {
        return JSON.parse(payload) as T;
      } catch {
        return fallback;
      }
    }

    if (payload && typeof payload === 'object') {
      return payload as T;
    }

    return fallback;
  }
}
